import { useState, useEffect, ChangeEvent } from "react";
import { storePssSelectTheme } from "../../layout/datasources/_pss.layout.store.slice";
import { assigneeAdapter } from "../../../types";
import { getAssigneeDetailsTableColumnsAdapter } from "../adapters";
import { useInputMultiSelect, useMedia } from "../../../../hooks";
import { useTypedSelector } from "../../../../store";
import {
  usePssAddAssigneeDetailsRepo,
  usePssAprsFindIncludeGetUserAssigneeRepo,
  usePssDeleteAssigneeDetailsRepo,
  usePssFindIncludeAssigneeDetailsRepo,
} from "../repositories";
import { Option } from "@coreui/react-pro/dist/components/multi-select/types";
import { getAssigneeDetailsFromResponse } from "../adapters/getAssigneeDetailsFromResponse";

export const usePssAssigneeDetails = () => {
  // preserved states
  const userActivePageString = sessionStorage.getItem(
    "pss-assignee-details-active-page",
  );
  const userItemsPerPageString = sessionStorage.getItem(
    "pss-assignee-details-items-per-page",
  );

  let userActivePage = 1;
  if (userActivePageString) {
    userActivePage = parseInt(userActivePageString);
  }

  let userItemsPerPage = 10;
  if (userItemsPerPageString) {
    userItemsPerPage = parseInt(userItemsPerPageString);
  }

  //states

  const theme = useTypedSelector(storePssSelectTheme);
  const media = useMedia();
  const [activePage, setActivePage] = useState<number>(userActivePage);
  const [itemsPerPage, setItemsPerPage] = useState<number>(userItemsPerPage);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [rowData, setRowData] = useState<assigneeAdapter | null>(null);
  const [validated, setValidated] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [successToastMessage, setSuccessToastMessage] = useState<string>("");
  const [filteredData, setFilteredData] = useState<assigneeAdapter[]>([]);
  const [searchAssigneeName, setSearchAssigneeName] = useState("");
  const [searchAssigneeNameValue, setSearchAssigneeNameValue] = useState("");
  const [newAssigneeModalVisible, setNewAssigneeModalVisible] =
    useState<boolean>(false);
  const [deleteAssigneeModalVisible, setDeleteAssigneeModalVisible] =
    useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");
  const [apiShowAlert, setApiShowAlert] = useState<boolean>(false);
  const [validName, setValidName] = useState(false);
  const [selectedNameOptions, setSelectedNameOptions] = useState<Option[]>([]);

  // Repositories
  const [addAssigneeDetails, addAssigneeDetailsApi] =
    usePssAddAssigneeDetailsRepo();

  const defaultForm = {
    name: [],
    userAssigneeId: [],
  };

  const [name, nameController] = useInputMultiSelect<string>(defaultForm.name, {
    validator: (_name: string[]) => {
      if (_name.length === 0) {
        return {
          valid: false,
          feedback: "Assignee Name is required!",
        };
      }
      return {
        valid: true,
        feedback: "",
      };
    },
    validated: validated,
    onFocus: () => {
      setFormError(null);
      setApiShowAlert(false);
    },
  });
  const [userAssigneeId, userIdController] = useInputMultiSelect<string>(
    defaultForm.userAssigneeId,
    {
      validator: (_userAssigneeId: string[]) => {
        if (_userAssigneeId.length === 0) {
          return {
            valid: false,
            feedback: "Assignee Name id is required!",
          };
        }
        return {
          valid: true,
          feedback: "",
        };
      },
      validated: validated,
      onFocus: () => {
        setFormError(null);
        setApiShowAlert(false);
      },
    },
  );
  useEffect(() => {
    sessionStorage.setItem(
      "pss-assignee-details-active-page",
      activePage.toString(),
    );
    sessionStorage.setItem(
      "pss-assignee-details-items-per-page",
      itemsPerPage.toString(),
    );
  }, [activePage, itemsPerPage]);

  //usecases
  const { state: assigneeState, action: assigneeAction } =
    usePssFindIncludeAssigneeDetailsRepo({
      page: activePage.toString(),
      item: itemsPerPage.toString(),
      name: searchAssigneeName,
    });
  const pages = assigneeState.assigneeData?.pagination
    ? Math.ceil(
        assigneeState.assigneeData?.pagination.totalCount / itemsPerPage,
      )
    : 1;

  const items = getAssigneeDetailsFromResponse(assigneeState.assigneeData);

  // events
  const refresh = async () => {
    try {
      const response = await assigneeAction.refetch().unwrap();
      console.log("response :", response);
    } catch (e) {
      console.error(e);
    }
  };

  const [currentSelection, setCurrentSelection] = useState<{
    name: {
      value: number | string;
      text: string;
      selected: boolean;
    };
    id: {
      value: number | string;
      text: string;
      selected: boolean;
    };
    department: {
      value: number | string;
      text: string;
      selected: boolean;
    };
    contactNo: {
      value: number | string;
      text: string;
      selected: boolean;
    };
    position: {
      value: number | string;
      text: string;
      selected: boolean;
    };
    email: {
      value: number | string;
      text: string;
      selected: boolean;
    };
  } | null>(null);

  const [searchAssigneesName, setSearchAssigneesName] = useState<string>("");
  const [searchAssigneesNameValue, setSearchAssigneesNameValue] =
    useState<string>("");
  const { state: findUserAssigneeStatus } =
    usePssAprsFindIncludeGetUserAssigneeRepo({ name: searchAssigneesName });
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const onAssigneeNameSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchAssigneesNameValue(inputValue);
    setSearchAssigneesName(inputValue);
  };

  const handleSearchButtonClick = () => {
    if (searchAssigneesNameValue) {
      setSearchAssigneesName(searchAssigneesNameValue);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };
  const selectedTableUsers = items.list
    ? items.list.map((item: assigneeAdapter) => item.name)
    : [];
  const itemSearch = findUserAssigneeStatus.userAssignee;

  const newNameOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.name))
        .map((assignee) => {
          return {
            value: assignee.name ?? 0,
            text: assignee.name ?? "-",
            selected: false,
          };
        })
    : [];
  const idOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.id))
        .map((assignee) => {
          return {
            value: assignee.id ?? 0,
            text: assignee.id ?? "-",
            selected: false,
          };
        })
    : [];
  const positionOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.position))
        .map((assignee) => {
          return {
            value: assignee.position ?? 0,
            text: assignee.position ?? "-",
            selected: false,
          };
        })
    : [];

  const contactNoOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.contactNo))
        .map((assignee) => {
          return {
            value: assignee.contactNo ?? 0,
            text: assignee.contactNo ?? "-",
            selected: false,
          };
        })
    : [];

  const departmentOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.department))
        .map((assignee) => {
          return {
            value: assignee.department ?? 0,
            text: assignee.department ?? "-",
            selected: false,
          };
        })
    : [];

  const emailOptions = itemSearch
    ? itemSearch
        .filter((assignee) => !selectedTableUsers.includes(assignee.email))
        .map((assignee) => {
          return {
            value: assignee.email ?? 0,
            text: assignee.email ?? "-",
            selected: false,
          };
        })
    : [];

  const onNewNameSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
      setValidName(false);
    } else {
      const currentUserOption = options[0];
      const nameOption = itemSearch?.find(
        (assignee) => assignee.name === currentUserOption.value,
      );

      setCurrentSelection({
        name: {
          value: currentUserOption.value,
          text: currentUserOption.text,
          selected: true,
        },
        id: {
          value: nameOption?.id ?? 0,
          text: nameOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: nameOption?.position ?? 0,
          text: nameOption?.position ?? "-",
          selected: true,
        },
        contactNo: {
          value: nameOption?.contactNo ?? 0,
          text: nameOption?.contactNo ?? "-",
          selected: true,
        },
        department: {
          value: nameOption?.department ?? 0,
          text: nameOption?.department ?? "-",
          selected: true,
        },
        email: {
          value: nameOption?.email ?? 0,
          text: nameOption?.email ?? "-",
          selected: true,
        },
      });
      setValidName(true);
    }
    setSelectedNameOptions(options);
  };

  const onNewIdSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
    } else {
      const currentOption = options[0];
      const nameIdOption = itemSearch?.find(
        (user) => user.id === currentOption.value,
      );
      setCurrentSelection({
        name: {
          value: nameIdOption?.name ?? 0,
          text: nameIdOption?.name ?? "-",
          selected: true,
        },
        id: {
          value: currentOption?.id ?? 0,
          text: currentOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: nameIdOption?.position ?? 0,
          text: nameIdOption?.position ?? "-",
          selected: true,
        },
        contactNo: {
          value: nameIdOption?.contactNo ?? 0,
          text: nameIdOption?.contactNo ?? "-",
          selected: true,
        },
        department: {
          value: nameIdOption?.department ?? 0,
          text: nameIdOption?.department ?? "-",
          selected: true,
        },
        email: {
          value: nameIdOption?.email ?? 0,
          text: nameIdOption?.email ?? "-",
          selected: true,
        },
      });
    }
  };
  const onNewPositionSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
    } else {
      const currentOption = options[0];
      const assigneePositionOption = itemSearch?.find(
        (assignee) => assignee.position === currentOption.value,
      );
      setCurrentSelection({
        name: {
          value: currentOption?.name ?? 0,
          text: currentOption?.name ?? "-",
          selected: true,
        },
        id: {
          value: assigneePositionOption?.id ?? 0,
          text: assigneePositionOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: currentOption.value,
          text: currentOption.text,
          selected: true,
        },
        contactNo: {
          value: currentOption?.contactNo ?? 0,
          text: currentOption?.contactNo ?? "-",
          selected: true,
        },
        department: {
          value: currentOption?.department ?? 0,
          text: currentOption?.department ?? "-",
          selected: true,
        },
        email: {
          value: currentOption?.email ?? 0,
          text: currentOption?.email ?? "-",
          selected: true,
        },
      });
    }
  };

  const onNewEmailSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
    } else {
      const currentOption = options[0];
      const assigneeEmailOption = itemSearch?.find(
        (assignee) => assignee.email === currentOption.value,
      );
      setCurrentSelection({
        name: {
          value: assigneeEmailOption?.name ?? 0,
          text: assigneeEmailOption?.name ?? "-",
          selected: true,
        },
        id: {
          value: assigneeEmailOption?.id ?? 0,
          text: assigneeEmailOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: assigneeEmailOption?.position ?? 0,
          text: assigneeEmailOption?.position ?? "-",
          selected: true,
        },
        contactNo: {
          value: assigneeEmailOption?.contactNo ?? 0,
          text: assigneeEmailOption?.contactNo ?? "-",
          selected: true,
        },
        department: {
          value: assigneeEmailOption?.department ?? 0,
          text: assigneeEmailOption?.department ?? "-",
          selected: true,
        },
        email: {
          value: currentOption.value,
          text: currentOption.text,
          selected: true,
        },
      });
    }
  };

  const onNewDepartmentSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
    } else {
      const currentOption = options[0];
      const assigneeDepartmentOption = itemSearch?.find(
        (assignee) => assignee.department === currentOption.value,
      );
      setCurrentSelection({
        name: {
          value: assigneeDepartmentOption?.name ?? 0,
          text: assigneeDepartmentOption?.name ?? "-",
          selected: true,
        },
        id: {
          value: assigneeDepartmentOption?.id ?? 0,
          text: assigneeDepartmentOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: assigneeDepartmentOption?.position ?? 0,
          text: assigneeDepartmentOption?.position ?? "-",
          selected: true,
        },
        contactNo: {
          value: assigneeDepartmentOption?.contactNo ?? 0,
          text: assigneeDepartmentOption?.contactNo ?? "-",
          selected: true,
        },
        department: {
          value: currentOption.value,
          text: currentOption.text,
          selected: true,
        },
        email: {
          value: assigneeDepartmentOption?.email ?? 0,
          text: assigneeDepartmentOption?.email ?? "-",
          selected: true,
        },
      });
    }
  };

  const onNewContactNoSelectionChange = (options: Option[]) => {
    if (options.length < 1) {
      setCurrentSelection(null);
    } else {
      const currentOption = options[0];
      const assigneeContactNoOption = itemSearch?.find(
        (assignee) => assignee.contactNo === currentOption.value,
      );
      setCurrentSelection({
        name: {
          value: assigneeContactNoOption?.name ?? 0,
          text: assigneeContactNoOption?.name ?? "-",
          selected: true,
        },
        id: {
          value: assigneeContactNoOption?.id ?? 0,
          text: assigneeContactNoOption?.id ?? "-",
          selected: true,
        },
        position: {
          value: assigneeContactNoOption?.position ?? 0,
          text: assigneeContactNoOption?.position ?? "-",
          selected: true,
        },
        contactNo: {
          value: currentOption.value,
          text: currentOption.text,
          selected: true,
        },
        department: {
          value: assigneeContactNoOption?.department ?? 0,
          text: assigneeContactNoOption?.department ?? "-",
          selected: true,
        },
        email: {
          value: assigneeContactNoOption?.email ?? 0,
          text: assigneeContactNoOption?.email ?? "-",
          selected: true,
        },
      });
    }
  };

  const showSuccessToastNotification = (message: string) => {
    setSuccessToastMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 3000);
  };

  const onSearchClicked = () => {
    setSearchAssigneeName(searchAssigneeNameValue);
  };

  const onSyncClicked = () => {
    setSearchAssigneeName("");
    setSearchAssigneeNameValue("");
    setFilteredData([]);
  };

  const onAssigneeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchAssigneeNameValue(e.target.value);
  };

  // Repositories

  const [deleteAssigneeDetails, deleteAssigneeDetailsApi] =
    usePssDeleteAssigneeDetailsRepo();

  useEffect(() => {
    if (addAssigneeDetails.isSuccess) {
      addAssigneeDetails.reset();
    }
  }, [addAssigneeDetails]);

  useEffect(() => {
    if (deleteAssigneeDetails.isSuccess) {
      deleteAssigneeDetails.reset();
    }
  }, [deleteAssigneeDetails]);

  const onAddNewAssignee = () => {
    setValidated(false);
    nameController.setValues([]);
    userIdController.setValues([]);
    setNewAssigneeModalVisible(!newAssigneeModalVisible);
  };

  const onAddNewAssigneeClose = () => {
    setNewAssigneeModalVisible(false);
  };

  const onDeleteAssignee = (item: assigneeAdapter | undefined) => {
    nameController.setValues([item?.name ?? ""]);
    setDeleteAssigneeModalVisible(!deleteAssigneeModalVisible);
    setUserId(item?.id ?? "");
  };

  const onDeleteAssigneeClose = () => {
    setValidated(false);
    setDeleteAssigneeModalVisible(false);
  };
  const onSubmit = async () => {
    setValidated(true);
    if (nameController.peek().invalid || userIdController.peek().invalid) {
      setNewAssigneeModalVisible(true);
    } else {
      const formData = new FormData();
      formData.append("name", name.values.toString());
      formData.append("userId", userAssigneeId.values.toString());

      try {
        const response = await addAssigneeDetailsApi(formData).unwrap();
        if (response) {
          setNewAssigneeModalVisible(false);
          showSuccessToastNotification("The assignee is added successfully!");
          nameController.setValues([]);
          userIdController.setValues([]);
          setValidated(false);
          setFormError(null);
          setApiShowAlert(false);
        }
      } catch (error) {
        setFormError("Failed to add assignee. Please try again.");
        setApiShowAlert(true);
      }
    }
  };
  const onDelete = (item: assigneeAdapter | undefined) => {
    deleteAssigneeDetailsApi(item?.id);
    setDeleteAssigneeModalVisible(false);
    showSuccessToastNotification("The assignee is deleted successfully!");
  };
  return [
    {
      columns: getAssigneeDetailsTableColumnsAdapter(theme, media),
      error: assigneeState.error,
      media,
      activePage,
      itemsPerPage,
      rowData,
      showSuccessToast,
      successToastMessage,
      newAssigneeModalVisible,
      deleteAssigneeModalVisible,
      validated,
      formError,
      userId,
      name,
      pages,
      items,
      filteredData,
      searchAssigneeName,
      searchAssigneeNameValue,
      currentSelection,
      selectedNameOptions,
      loading: assigneeState.isLoading,
      validName,
      newNameOptions,
      idOptions,
      emailOptions,
      positionOptions,
      contactNoOptions,
      departmentOptions,
      findUserAssigneeStatus,
      searchAssigneesNameValue,
      showDropdown,
      deleteVisible,
      apiShowAlert,
      userAssigneeId,
    },
    {
      setUserId,
      setRowData: setRowData,
      setDeleteVisible: setDeleteVisible,
      onDeleteAssignee,
      onSubmit,
      onDelete,
      onDeleteAssigneeClose,
      onAddNewAssignee,
      onAddNewAssigneeClose,
      setValidated: setValidated,
      refresh,
      nameController: nameController,
      setActivePage: setActivePage,
      setItemsPerPage: setItemsPerPage,
      onSearchClicked,
      onSyncClicked,
      onAssigneeSearch,
      onNewEmailSelectionChange,
      onNewContactNoSelectionChange,
      onNewDepartmentSelectionChange,
      onNewPositionSelectionChange,
      onNewIdSelectionChange,
      onNewNameSelectionChange,
      setSearchAssigneesNameValue,
      handleSearchButtonClick,
      onAssigneeNameSearch,
      setApiShowAlert,
      showSuccessToastNotification,
      userIdController,
      name,
    },
  ] as const;
};
